import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trophy, ChevronRight, Star, Loader2, PanelLeftClose, PanelLeftOpen, ChevronDown, Search, X } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useCached } from "@/lib/fbCache";
import { useFbFavs } from "@/lib/useFbFavs";
import { AdSlot } from "@/components/AdSlot";
import { MatchRow } from "@/components/football/MatchRow";
import { FootballSidebar } from "@/components/football/FootballSidebar";
import { CacheBadge } from "@/components/football/CacheBadge";
import { HighlightsStrip } from "@/components/football/Highlights";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { DateBar, todayStr, dayLabel, needsPoll, H } from "@/components/football/DateBar";

function LeagueGroup({ g, comp }) {
  return (
    <div className="mb-5" data-testid={`matches-league-${g.leagueId}`}>
      <div className="flex items-center gap-3 px-1 mb-2">
        <span className="w-8 h-8 rounded-lg bg-[#0A0C10] border border-[#273042] flex items-center justify-center shrink-0">
          {g.logo ? <img src={g.logo} alt="" loading="lazy" className="w-5 h-5 object-contain" /> : <Trophy className="w-4 h-4 text-[#E11D2A]" />}
        </span>
        <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-white truncate">{g.league}</span>
          {comp?.country && <span className="block text-[11px] text-slate-500">{comp.country}</span>}</span>
        {g.slug && <Link to={`/football/${g.slug}`} data-testid={`league-open-${g.leagueId}`} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-0.5">Table <ChevronRight className="w-4 h-4" /></Link>}
      </div>
      <div className="space-y-2">{g.matches.map((m) => <MatchRow key={m.id} f={m} />)}</div>
    </div>
  );
}

export default function Football() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const date = sp.get("date") || todayStr();
  const view = sp.get("view") || "day";
  const compSlug = sp.get("comp") || "";
  const setParam = (k, v) => setSp((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const [search, setSearch] = useState("");
  const [sheet, setSheet] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("bbk_fb_sidebar") === "0");
  const { favs } = useFbFavs();

  const compsQ = useCached("comps", () => endpoints.footballCompetitions(), 6 * H);
  const comps = useMemo(() => compsQ.data?.competitions || [], [compsQ.data]);
  const selected = comps.find((c) => c.slug === compSlug) || null;
  const smComps = comps.filter((c) => c.provider === "sportmonks" && c.smLeagueId);
  const leagues = selected?.smLeagueId ? String(selected.smLeagueId) : smComps.map((c) => c.smLeagueId).sort((a, b) => a - b).join(",");
  const compByLeague = useMemo(() => Object.fromEntries(comps.filter((c) => c.smLeagueId).map((c) => [c.smLeagueId, c])), [comps]);

  const isToday = date === todayStr();
  const ttl = isToday ? 60e3 : date < todayStr() ? 6 * H : 30 * 60e3;
  const [poll, setPoll] = useState(0);
  const dayKey = compsQ.data && view !== "live" ? `m:${date}:${leagues}` : null;
  const dayQ = useCached(dayKey, () => endpoints.footballMatches(date, leagues), ttl, isToday ? poll : 0);
  const liveQ = useCached(view === "live" ? "live" : null, () => endpoints.footballLive(), 55e3, 60e3);
  const hlQ = useCached("hl", () => endpoints.footballHighlights("", 24), 10 * 60e3);

  const q = search.trim().toLowerCase();
  const allowed = new Set(leagues.split(",").filter(Boolean).map(Number));
  const favMatchIds = new Set(favs.matches.map((m) => String(m.id)));
  const favTeamIds = new Set(favs.teams.map((t) => String(t.id)));
  const favCompSlugs = new Set(favs.competitions.map((c) => c.id));
  const keep = (m) => (!q || `${m.home?.name} ${m.away?.name} ${m.league}`.toLowerCase().includes(q)) &&
    (view !== "fav" || favMatchIds.has(String(m.id)) || favTeamIds.has(String(m.home?.id)) || favTeamIds.has(String(m.away?.id)) || favCompSlugs.has(compByLeague[m.leagueId]?.slug));

  const groups = useMemo(() => {
    let src;
    if (view === "live") {
      const by = {};
      (liveQ.data?.matches || []).filter((m) => !allowed.size || allowed.has(Number(m.leagueId))).forEach((m) => {
        (by[m.leagueId] = by[m.leagueId] || { leagueId: m.leagueId, league: m.league, logo: m.leagueLogo, slug: compByLeague[m.leagueId]?.slug, matches: [] }).matches.push(m);
      });
      src = Object.values(by);
    } else src = dayQ.data?.leagues || [];
    const rank = (g) => (favCompSlugs.has(compByLeague[g.leagueId]?.slug) ? -1000 : 0) + (compByLeague[g.leagueId]?.order ?? 500);
    return src.map((g) => ({ ...g, matches: g.matches.filter(keep) })).filter((g) => g.matches.length).sort((a, b) => rank(a) - rank(b));
  }, [view, liveQ.data, dayQ.data, q, favs, leagues, compByLeague]); // eslint-disable-line react-hooks/exhaustive-deps

  const flat = groups.flatMap((g) => g.matches);
  const wantPoll = isToday && view !== "live" && needsPoll((dayQ.data?.leagues || []).flatMap((g) => g.matches)) ? 60e3 : 0;
  useEffect(() => { setPoll(wantPoll); }, [wantPoll]);
  const liveCount = (dayQ.data?.leagues || []).flatMap((g) => g.matches).filter((m) => m.status === "live" && isToday).length || (liveQ.data?.matches || []).length;
  const active = view === "live" ? liveQ : dayQ;

  const pick = (c) => {
    setSheet(false);
    if (c && c.provider !== "sportmonks") { nav(`/football/${c.slug}`); return; }
    setParam("comp", c?.slug || "");
  };
  const toggleCollapse = () => { localStorage.setItem("bbk_fb_sidebar", collapsed ? "1" : "0"); setCollapsed(!collapsed); };
  const sidebarProps = { comps, selected, onPick: pick, search, setSearch };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10" data-testid="football-page">
      <div className="flex items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white flex items-center gap-3"><Trophy className="w-8 h-8 sm:w-9 sm:h-9 text-[#E11D2A]" /> Football</h1>
          <p className="text-slate-400 text-sm mt-1.5">Live scores, fixtures, results and match centre.</p>
        </div>
        <button onClick={toggleCollapse} data-testid="fb-sidebar-toggle" className="hidden lg:inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[#273042] text-sm text-slate-300 hover:text-white">
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}{collapsed ? "Show competitions" : "Hide panel"}
        </button>
      </div>
      <AdSlot placement="football" position="top" className="mb-5" />

      <div className={`grid gap-5 ${collapsed ? "" : "lg:grid-cols-[290px_minmax(0,1fr)]"}`}>
        {!collapsed && <aside className="hidden lg:block"><div className="sticky top-20"><FootballSidebar {...sidebarProps} /></div></aside>}

        <main className="min-w-0 space-y-3">
          <div className="lg:hidden flex gap-2">
            <button onClick={() => setSheet(true)} data-testid="fb-mobile-selector"
              className="flex-1 min-w-0 h-11 px-3 rounded-xl bg-[#0F131B] border border-[#273042] flex items-center gap-2 text-left">
              {selected?.logoUrl ? <img src={selected.logoUrl} alt="" className="w-5 h-5 object-contain" /> : <Trophy className="w-4 h-4 text-[#E11D2A]" />}
              <span className="flex-1 truncate text-sm text-white font-semibold">{selected ? selected.name : "All competitions"}</span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
            <label className="w-[42%] h-11 px-3 rounded-xl bg-[#0F131B] border border-[#273042] flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-500 shrink-0" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" data-testid="fb-mobile-search" className="w-full min-w-0 bg-transparent text-sm text-white outline-none placeholder-slate-500" />
            </label>
          </div>

          <DateBar date={date} setDate={(d) => setParam("date", d === todayStr() ? "" : d)} view={view} setView={(v) => setParam("view", v === "day" ? "" : v)} liveCount={liveCount} />

          <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
            <button onClick={() => setParam("view", view === "fav" ? "" : "fav")} data-testid="fb-fav-filter" aria-pressed={view === "fav"}
              className={`h-8 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 whitespace-nowrap border transition-colors ${view === "fav" ? "bg-amber-400/15 border-amber-400/50 text-amber-300" : "border-[#273042] text-slate-300 hover:text-white"}`}>
              <Star className={`w-3.5 h-3.5 ${view === "fav" ? "fill-amber-400 text-amber-400" : ""}`} /> Favourites
            </button>
            {selected && <button onClick={() => setParam("comp", "")} data-testid="fb-clear-comp" className="h-8 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 whitespace-nowrap bg-[#E11D2A]/15 text-white border border-[#E11D2A]/40">{selected.name}<X className="w-3.5 h-3.5" /></button>}
            {selected && <Link to={`/football/${selected.slug}`} data-testid="fb-open-comp" className="h-8 px-3 rounded-full text-xs font-semibold inline-flex items-center whitespace-nowrap border border-[#273042] text-slate-300 hover:text-white">Standings & fixtures <ChevronRight className="w-3.5 h-3.5" /></Link>}
            {q && <span className="text-xs text-slate-500 whitespace-nowrap" data-testid="fb-search-summary">{flat.length} match{flat.length === 1 ? "" : "es"} for “{search}”</span>}
          </div>

          <CacheBadge savedAt={active.savedAt} error={active.error} loading={active.loading}
            staleAfter={view === "live" || (isToday && needsPoll(flat)) ? 2 * 60e3 : isToday ? 15 * 60e3 : 12 * H} onRefresh={active.reload} />

          <section data-testid="matches-by-day">
            {!active.data && active.loading ? <div className="flex justify-center py-14"><Loader2 className="w-6 h-6 animate-spin text-[#E11D2A]" /></div>
              : !active.data && active.error ? <p className="text-slate-400 text-center py-10" data-testid="matches-error">Scores are unavailable right now. Check your connection and tap Refresh.</p>
              : groups.length === 0 ? <p className="text-slate-400 text-center py-10 rounded-xl border border-dashed border-[#273042]" data-testid="matches-empty">
                {view === "live" ? "No live matches right now in the selected competitions." : view === "fav" ? "No favourite matches on this day. Tap the star on a match, team or competition to add it." : q ? "No matches match your search." : `No matches on ${dayLabel(date).toLowerCase()} in the selected competitions.`}</p>
              : groups.map((g) => <LeagueGroup key={g.leagueId} g={g} comp={compByLeague[g.leagueId]} />)}
          </section>

          <HighlightsStrip items={hlQ.data?.highlights} />
        </main>
      </div>

      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent side="left" className="w-[88vw] max-w-sm p-3 bg-[#0A0C10] border-[#273042] [&>button]:hidden" data-testid="fb-mobile-sheet">
          <FootballSidebar {...sidebarProps} onClose={() => setSheet(false)} />
        </SheetContent>
      </Sheet>
      <AdSlot placement="football" position="bottom" className="mt-8" />
    </div>
  );
}
